// store/modules/category.js
import api from "@/utils/axios";

const CATEGORY_API_URL = `/categories`;

export default {
  namespaced: true,
  state: {
    category: null,
    categories: [],
    meta: {},
    loading: false,
    error: null,
  },
  mutations: {
    SET_CATEGORY(state, category) {
      state.category = category;
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    SET_META(state, meta) {
      state.meta = meta;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_CATEGORY(state) {
      state.category = null;
      state.error = null;
    },
    CLEAR_CATEGORIES(state) {
      state.categories = [];
      state.meta = {};
      state.error = null;
    },
  },
  actions: {
    async fetchCategory({ commit }, categoryId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${CATEGORY_API_URL}/${categoryId}/`);
        commit("SET_CATEGORY", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch category"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async selectCategories({ commit }, searchTerm = "") {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${CATEGORY_API_URL}/select/`, {
          params: { q: searchTerm },
        });
        return res.data.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch categories"
        );
        return [];
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async filterCategories({ commit }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${CATEGORY_API_URL}/filter/`, payload);
        commit("SET_CATEGORIES", res.data.data);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch categories"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async lazyLoadCategories({ commit, state }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${CATEGORY_API_URL}/filter/`, payload);
        const newCategories = res.data.data;
        const updatedCategories = [...state.categories, ...newCategories];
        commit("SET_CATEGORIES", updatedCategories);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to load more categories"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewCategory({ commit }, data) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${CATEGORY_API_URL}/new/`, data);
        commit("SET_CATEGORY", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to create category"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateCategory({ commit }, { categoryId, data }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(`${CATEGORY_API_URL}/${categoryId}/`, data);
        commit("SET_CATEGORY", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to update category"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteCategory({ commit }, categoryId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        await api.delete(`${CATEGORY_API_URL}/${categoryId}/`);
        commit("CLEAR_CATEGORY");
        return true;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to delete category"
        );
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    clearCategory({ commit }) {
      commit("CLEAR_CATEGORY");
    },

    clearCategories({ commit }) {
      commit("CLEAR_CATEGORIES");
    },
  },
  getters: {
    category: (state) => state.category,
    categories: (state) => state.categories,
    categoryMeta: (state) => state.meta,
    categoryLoading: (state) => state.loading,
    categoryError: (state) => state.error,
  },
};
